import React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { ArrowRight, BarChart2, ScanLine, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PillTag } from '@/components/pill-tag';
import { QRBuilder } from '@/features/qr-builder';
import { container, fadeUp } from '@/constants/motion';
export const Route = createFileRoute('/_layout/qr-builder/')({
    component: PublicQRBuilderPage,
});
function PublicQRBuilderPage() {
    return (<motion.main initial="hidden" animate="visible" variants={container} className="min-h-screen bg-background">
            {/* Hero */}
            <section className="border-b bg-muted/30">
                <div className="mx-auto max-w-5xl px-4 py-12 md:py-16 text-center">
                    <motion.div variants={fadeUp} className="space-y-4">
                        <PillTag label="Free QR Code Generator"/>
                        <h1 className="text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">
                            Design beautiful QR codes
                            <br className="hidden sm:block"/>
                            <span className="text-primary"> in seconds</span>
                        </h1>
                        <p className="mx-auto max-w-xl text-sm text-muted-foreground md:text-base">
                            Customise dots, eyes, colours, frames and logos. Export as PNG or SVG — no account required.
                            Upgrade to track every scan with full analytics.
                        </p>
                    </motion.div>

                    <motion.div variants={fadeUp} className="mt-6 flex flex-wrap items-center justify-center gap-3">
                        <Button asChild>
                            <Link to="/sign-up" search={{ redirect: '/console' }}>
                                Get scan analytics free
                                <ArrowRight className="ml-1.5 h-4 w-4"/>
                            </Link>
                        </Button>
                        <Button variant="ghost" asChild>
                            <Link to="/sign-in" search={{ redirect: '/console' }}>
                                Sign in
                            </Link>
                        </Button>
                    </motion.div>
                </div>
            </section>

            {/* Builder */}
            <section className="mx-auto max-w-7xl px-4 py-10 md:py-14">
                <motion.div variants={fadeUp}>
                    <QRBuilder />
                </motion.div>
            </section>

            {/* Analytics upsell banner */}
            <section className="border-t bg-muted/40">
                <div className="mx-auto max-w-5xl px-4 py-12">
                    <motion.div variants={fadeUp} className="rounded-2xl border bg-card p-8 text-center shadow-sm">
                        <h2 className="text-xl font-semibold md:text-2xl">Want to know who&apos;s scanning?</h2>
                        <p className="mt-2 text-sm text-muted-foreground md:text-base">
                            Adora turns every QR scan into a structured data point — device, location, time, and what
                            the user actually wanted. Free forever for the first campaign.
                        </p>

                        <div className="mt-8 grid gap-4 sm:grid-cols-3">
                            <UpsellFeature icon={<ScanLine className="h-5 w-5"/>} title="Track every scan" body="See when, where, and on what device your QR codes are scanned."/>
                            <UpsellFeature icon={<BarChart2 className="h-5 w-5"/>} title="Measure intent" body="Know which destination users clicked, not just that they scanned."/>
                            <UpsellFeature icon={<Zap className="h-5 w-5"/>} title="Go live in minutes" body="One QR code, unlimited destinations. Change the link without reprinting."/>
                        </div>

                        <div className="mt-8">
                            <Button size="lg" asChild>
                                <Link to="/sign-up" search={{ redirect: '/console' }}>
                                    Start tracking for free
                                    <ArrowRight className="ml-1.5 h-4 w-4"/>
                                </Link>
                            </Button>
                        </div>
                    </motion.div>
                </div>
            </section>
        </motion.main>);
}
function UpsellFeature({ icon, title, body }) {
    return (<div className="flex flex-col items-center gap-2 rounded-xl border bg-background p-5 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                {icon}
            </div>
            <p className="text-sm font-semibold">{title}</p>
            <p className="text-xs text-muted-foreground">{body}</p>
        </div>);
}
